import React, { useContext, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BellRinging,
  CaretDown,
  FolderSimple,
  List,
  MagnifyingGlass,
  SignOut,
  X,
} from '@phosphor-icons/react';
import { AppCtx } from '../AppContext';
import { contextualSearch, PRIMARY_NAV, routeAccess, SETTINGS_NAV } from '../app-shell/routes';
import ProfileModal from './ProfileModal';

const ROLE_LABELS = {
  facilitator: 'Facilitator',
  executive: 'Executive',
  participant: 'Participant',
  super_admin: 'Admin',
  admin: 'Admin',
  viewer: 'Viewer',
};

const LogoMark = () => (
  <svg viewBox="0 0 64 64" width="32" height="32" aria-hidden>
    <rect width="64" height="64" rx="16" fill="#05070D" />
    <g fill="none" stroke="white" strokeWidth="3">
      <circle cx="32" cy="32" r="22" />
      <circle cx="32" cy="32" r="10" />
    </g>
    <path d="M32 28v8M28 32h8" stroke="white" strokeWidth="3" strokeLinecap="square" />
  </svg>
);

function NavItem({ to, icon: Icon, label, sub, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === '/boardroom' || to === '/settings'}
      onClick={onClick}
      data-testid={`nav-${to.replaceAll('/', '') || 'boardroom'}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${
          isActive ? 'bg-white/[0.06] text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded before:bg-white' : 'text-slate-300 hover:bg-white/[0.035] hover:text-white'
        }`
      }
    >
      <Icon size={18} weight="duotone" className="shrink-0 text-slate-400 group-hover:text-white" aria-hidden />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold">{label}</div>
        {sub && <div className="truncate text-[10px] text-slate-400 group-hover:text-slate-300">{sub}</div>}
      </div>
    </NavLink>
  );
}

export default function Layout() {
  const {
    user,
    logout,
    t,
    search,
    setSearch,
    enterpriseV2,
    programId,
    programName,
    availablePrograms,
    setProgramId,
  } = useContext(AppCtx);
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [programMenuOpen, setProgramMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef(null);
  const programMenuRef = useRef(null);

  useEffect(() => {
    const fallback = user?.role === 'participant' ? '/work' : '/boardroom';
    if (user && !routeAccess(location.pathname, user.role)) navigate(fallback, { replace: true });
  }, [location.pathname, user?.role, navigate, user]);

  useEffect(() => {
    setMobileNavOpen(false);
    setUserMenuOpen(false);
    setProgramMenuOpen(false);
    setSearch('');
  }, [location.pathname, setSearch]);

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const close = (event) => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !userMenuRef.current?.contains(event.target))) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    if (!programMenuOpen) return undefined;
    const close = (event) => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !programMenuRef.current?.contains(event.target))) setProgramMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [programMenuOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  const roleLabel = ROLE_LABELS[user?.role] || ROLE_LABELS.viewer;
  const filterLabel = contextualSearch(location.pathname);
  const visiblePrimaryNav = PRIMARY_NAV.filter((item) => item.roles.includes(user?.role));

  const SidebarContent = ({ mobile = false }) => {
    const closeAfterMobileNavigation = mobile ? () => setMobileNavOpen(false) : undefined;

    return (
      <>
      <div className="flex items-center gap-3 border-b border-slate-800 px-5 py-5">
        <LogoMark />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-lg font-bold leading-none">{t.appName}</div>
          <div className="mt-1 text-[10px] tracking-[0.18em] text-slate-400">Execution Intelligence</div>
        </div>
        {mobile && (
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="tkp-icon-btn border-slate-700 bg-transparent text-slate-300 hover:border-slate-500 hover:text-white"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-hidden px-2 py-5" aria-label="Main navigation">
        {visiblePrimaryNav.map((item) => (
          <NavItem key={item.to} {...item} onClick={closeAfterMobileNavigation} />
        ))}
      </nav>
      <div className="shrink-0 border-t border-slate-800 px-2 py-3">
        {SETTINGS_NAV.roles.includes(user?.role) && <NavItem {...SETTINGS_NAV} onClick={closeAfterMobileNavigation} />}
      </div>
      </>
    );
  };

  return (
    <div className="flex min-h-[100dvh] min-w-0 bg-canvas lg:h-[100dvh] lg:min-h-0 lg:overflow-hidden" data-testid="app-shell">
      <aside className="tkp-sidebar hidden h-full min-h-0 w-[240px] shrink-0 flex-col overflow-hidden bg-[var(--ink-950)] text-white lg:flex" data-testid="sidebar">
        <SidebarContent />
      </aside>

      <div
        className={`fixed inset-0 z-40 bg-slate-950/50 transition-opacity lg:hidden ${mobileNavOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`tkp-sidebar fixed inset-y-0 left-0 z-50 flex w-[320px] max-w-[86vw] flex-col bg-[var(--ink-950)] text-white shadow-2xl transition-transform duration-200 ease-out lg:hidden ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
        role="dialog"
        aria-label="Mobile navigation"
        aria-modal={mobileNavOpen ? 'true' : undefined}
        aria-hidden={!mobileNavOpen}
        inert={mobileNavOpen ? undefined : ''}
      >
        <SidebarContent mobile />
      </aside>

      <main className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-clip">
        <header className="sticky top-0 z-30 flex min-h-[62px] shrink-0 items-center gap-2 border-b border-[var(--line)] bg-[var(--surface-card)]/95 px-3 py-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-[var(--surface-card)]/90 sm:gap-3 sm:px-5 lg:static lg:px-8" data-testid="topbar">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="tkp-icon-btn shrink-0 lg:hidden"
            aria-label="Open navigation"
            aria-expanded={mobileNavOpen}
          >
            <List size={19} weight="bold" />
          </button>

          {enterpriseV2 && (
            <div className="relative shrink-0" data-testid="program-selector" ref={programMenuRef}>
              <button
                type="button"
                data-testid="program-selector-btn"
                disabled={!(availablePrograms && availablePrograms.length > 0)}
                onClick={() => {
                  if (availablePrograms && availablePrograms.length > 0) {
                    setProgramMenuOpen((o) => !o);
                  }
                }}
                className={`flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface-sunken)] px-2.5 py-1.5 text-xs font-semibold text-slate-900 ${
                  availablePrograms && availablePrograms.length > 0
                    ? 'hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400'
                    : 'cursor-not-allowed opacity-60'
                }`}
                aria-haspopup="listbox"
                aria-expanded={programMenuOpen}
                aria-label="Select Program"
              >
                <FolderSimple size={14} className="text-slate-500" aria-hidden />
                <span className="max-w-[140px] truncate sm:max-w-[220px]">
                  {programName || (availablePrograms && availablePrograms.length > 0 ? 'Select Program' : 'No Programs Available')}
                </span>
                {availablePrograms && availablePrograms.length > 0 && (
                  <CaretDown size={12} className="text-slate-400" aria-hidden />
                )}
              </button>
              {programMenuOpen && availablePrograms && availablePrograms.length > 0 && (
                <div
                  role="listbox"
                  aria-label="Programs"
                  className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-md border border-[var(--line)] bg-white p-1 shadow-lg"
                >
                  {availablePrograms.map((prog) => (
                    <button
                      key={prog.id}
                      type="button"
                      role="option"
                      aria-selected={prog.id === programId}
                      onClick={() => {
                        if (setProgramId) setProgramId(prog.id);
                        setProgramMenuOpen(false);
                      }}
                      className={`flex w-full items-center justify-between rounded px-2.5 py-1.5 text-left text-xs ${
                        prog.id === programId
                          ? 'bg-slate-100 font-bold text-slate-950'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate">{prog.name}</span>
                      {prog.status && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                          {prog.status}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {filterLabel ? (
            <div className="relative min-w-0 flex-1 sm:max-w-[380px]">
              <MagnifyingGlass size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" aria-hidden />
              <input
                data-testid="global-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="tkp-input tkp-search-input h-10 w-full"
                placeholder={filterLabel}
                aria-label={filterLabel}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ink" aria-label="Clear filter">
                  <X size={14} />
                </button>
              )}
            </div>
          ) : <div className="min-w-0 flex-1" />}

          <div className="ml-auto flex items-center gap-2.5">
            <span className="hidden lg:inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-sunken)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-700)] border border-[var(--line-soft)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--risk-low)]" aria-hidden />
              Synthetic demo data
            </span>

            {enterpriseV2 && (
              <button
                type="button"
                data-testid="notifications-btn"
                className="tkp-icon-btn text-slate-500 hover:text-slate-900"
                aria-label="Notifications"
                onClick={() => navigate('/settings')}
              >
                <BellRinging size={18} />
              </button>
            )}
          </div>


          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              data-testid="user-avatar"
              onClick={() => setUserMenuOpen((open) => !open)}
              className="flex shrink-0 items-center gap-3 rounded-md px-1 py-1.5 text-left transition hover:bg-[var(--surface-sunken)] sm:px-2"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ink-950)] text-xs font-bold tracking-wider text-white">
                {user?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'AN'}
              </div>
              <div className="hidden min-w-[150px] leading-tight md:block">
                <div className="text-sm font-semibold text-slate-950">{user?.full_name}</div>
                <div className="mt-0.5 max-w-[180px] truncate text-[10px] uppercase tracking-[0.08em] text-[var(--ink-400)]">{roleLabel} - {user?.email}</div>
              </div>
            </button>
            {userMenuOpen && (
              <div role="menu" aria-label="User menu" className="absolute right-0 top-full z-50 mt-2 w-64 rounded-md border border-[var(--line)] bg-white p-2 shadow-xl">
                <div className="border-b border-[var(--line)] px-3 py-2">
                  <div className="truncate text-sm font-semibold">{user?.full_name}</div>
                  <div className="mt-1 truncate text-xs text-[var(--ink-500)]">{user?.email}</div>
                </div>
                <button type="button" onClick={() => { setUserMenuOpen(false); setProfileOpen(true); }} className="mt-1 w-full rounded px-3 py-2 text-left text-sm font-semibold hover:bg-[var(--surface-sunken)]">Profile and preferences</button>
                <button type="button" onClick={() => { setUserMenuOpen(false); navigate('/settings'); }} className="w-full rounded px-3 py-2 text-left text-sm font-semibold hover:bg-[var(--surface-sunken)]">Settings</button>
                <button type="button" onClick={logout} className="mt-1 flex w-full items-center gap-2 border-t border-[var(--line)] px-3 py-2 pt-3 text-left text-sm font-semibold hover:bg-[var(--surface-sunken)]">
                  <SignOut size={16} />{t.signOut}
                </button>
              </div>
            )}
          </div>
        </header>

        {(() => {
          const isFullBleed = location.pathname === '/chat' || location.pathname === '/worktree' || location.pathname.startsWith('/work/');
          return (
            <div
              className={
                isFullBleed
                  ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                  : "min-h-0 flex-1 px-4 py-4 sm:px-5 lg:overflow-y-auto lg:overscroll-contain lg:px-8 lg:py-6"
              }
              data-testid="workspace-scroll"
            >
              <React.Suspense fallback={(
                <div className="space-y-3 p-6" role="status" aria-live="polite">
                  <span className="sr-only">Loading workspace</span>
                  <div className="h-24 animate-pulse rounded-md border border-slate-200 bg-white" aria-hidden="true" />
                  <div className="h-48 animate-pulse rounded-md border border-slate-200 bg-white" aria-hidden="true" />
                </div>
              )}>
                <Outlet />
              </React.Suspense>
            </div>
          );
        })()}
      </main>

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
